/**
 * Reply-signature template hook + render helper.
 *
 * The template is one admin-editable system_settings row
 * (``reply.signature_template``). The agent reply composer pre-fills
 * its body with the template the first time a ticket is opened with
 * the public-reply tab and an empty composer.
 *
 * Template format: plain text with line breaks. Two special transforms
 * happen at render time:
 *   1. The literal placeholder ``{first_name}`` is replaced with the
 *      requester's first name (split on whitespace).
 *   2. Each line of the template becomes its own ``<p>`` and each
 *      blank line becomes ``<p></p>``. The ``prose`` CSS class adds
 *      vertical margin between adjacent ``<p>``s by default, but the
 *      TipTap ``TightParagraph`` extension overrides that with an
 *      inline ``margin: 0; min-height: 1.2em`` on every ``<p>`` it
 *      emits - so the multi-paragraph form renders compactly in both
 *      the editor and the rendered conversation pane, AND it lets the
 *      agent press Enter inside the seeded signature and get a real
 *      paragraph break (instead of a trailing ``<br>`` that the
 *      contenteditable displays as a blank line but disappears in
 *      static render). Bare URLs (``http://...``, ``https://...``)
 *      and email addresses are auto-wrapped in ``<a href>`` so the
 *      rendered email body shows clickable links.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { ReplySignatureResponse } from "@/types/api";

export const REPLY_SIGNATURE_QUERY_KEY = ["replySignature"] as const;

/**
 * Fetch the plain-text reply-signature template. Cached for the
 * session - admins editing it on /admin/settings can manually
 * invalidate by reloading the agent tab.
 */
export function useReplySignature(): UseQueryResult<ReplySignatureResponse> {
  return useQuery({
    queryKey: REPLY_SIGNATURE_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<ReplySignatureResponse>(
        "/agent/reply-signature",
      );
      return response.data;
    },
    // The template rarely changes; refetch on window focus is enough.
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------------
// Render helpers (pure - exported for unit tests)
// ---------------------------------------------------------------------

/** Replace HTML-special characters with their entities. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Wrap bare URLs and email addresses in an ``<a>`` so the rendered
 * email body has clickable links. Inputs are already HTML-escaped.
 *
 * The URL regex deliberately stops at whitespace and at `<` so an
 * already-linked piece is not double-wrapped. Trailing punctuation
 * (``.,;:!?)`` - common at end of a sentence) is excluded from the
 * captured URL so we don't break the sentence's grammar.
 */
function linkify(escapedLine: string): string {
  const urlRe = /\bhttps?:\/\/[^\s<]+?(?=[)\].,;:!?]?(?:\s|$))/g;
  const emailRe = /\b[\w.+-]+@[\w.-]+\.\w{2,}\b/g;
  return escapedLine
    .replace(urlRe, (m) => `<a href="${m}">${m}</a>`)
    .replace(
      emailRe,
      (m) => `<a href="mailto:${m}">${m}</a>`,
    );
}

/**
 * Convert a plain-text signature template into the HTML the TipTap
 * editor expects. Each line of the template becomes its own ``<p>``
 * and each blank line becomes ``<p></p>``. When the seed HTML is
 * loaded into the editor, ``TightParagraph`` re-emits each paragraph
 * with ``style="margin: 0; min-height: 1.2em"``, which both renders
 * empty paragraphs at visible height AND keeps the spacing compact
 * in the rendered conversation pane / outbound email.
 *
 * The ``{first_name}`` placeholder is substituted before line
 * processing so a placeholder mid-line works correctly. Passing an
 * empty ``firstName`` falls back to "there" so the rendered greeting
 * stays grammatical.
 */
// Inline style that the TipTap ``TightParagraph`` extension applies to
// every paragraph it emits. Emitting it on the seed HTML keeps the
// admin signature-preview pane (which renders this HTML directly,
// without going through TipTap) visually identical to the agent-side
// editor and the rendered conversation pane.
const P_STYLE = 'style="margin: 0; min-height: 1.2em"';

export function renderReplySignatureHtml(
  template: string,
  firstName: string,
): string {
  const safeFirst = (firstName || "").trim() || "there";
  const substituted = template.replaceAll("{first_name}", safeFirst);
  // Drop a single trailing newline if present so the template doesn't
  // produce an extra dangling empty paragraph after the final line.
  const trimmed = substituted.endsWith("\n")
    ? substituted.slice(0, -1)
    : substituted;
  return trimmed
    .split("\n")
    .map((line) => {
      if (line.length === 0) return `<p ${P_STYLE}></p>`;
      return `<p ${P_STYLE}>${linkify(escapeHtml(line))}</p>`;
    })
    .join("");
}

/**
 * Best-effort first-name extraction from a free-form display name.
 *
 * Handles "First Last", "Last, First", "Last, First Middle", and
 * trailing punctuation on the resulting token. Mirrors the backend
 * helper at backend/app/utils/first_name.py so the AI-draft path
 * (wrap_ai_draft_body) and the agent composer (this hook seeds the
 * signature template with {first_name}) substitute identically.
 *
 * Examples:
 *   "Raquel Charles"     -> "Raquel"     (First Last)
 *   "Charles, Raquel"    -> "Raquel"     (Last, First - common in
 *                                          business signatures)
 *   "Charles, Raquel A." -> "Raquel"
 *   "Raquel,"            -> "Raquel"
 *   ""                   -> ""
 */
export function firstNameOf(fullName: string | null | undefined): string {
  if (!fullName) return "";
  const trimmed = fullName.trim();
  if (!trimmed) return "";
  // An email address is not a name. A ticket that arrived by email
  // with no display name snapshots the address as requester_name, and
  // "Hi marge@profitmarginsinc.com," reads like a broken mail merge
  // (Greg, 2026-07-30). Returning "" lets the caller's existing
  // "there" fallback take over.
  if (trimmed.includes("@") && !/\s/.test(trimmed)) return "";
  // "Last, First" pattern: take the part after the first comma.
  let candidate = trimmed;
  const commaIdx = trimmed.indexOf(",");
  if (commaIdx >= 0) {
    const head = trimmed.slice(0, commaIdx).trim();
    const tail = trimmed.slice(commaIdx + 1).trim();
    candidate = tail.length > 0 ? tail : head;
  }
  const first = candidate.split(/\s+/)[0] ?? "";
  return first.replace(/[,.;:!?]+$/g, "").trim();
}
